import { defineEvent } from "@visioni/a3s-analytics";
import type { ProductInterest } from "@visioni/a3s-upsell";
import type { AlarmFeatures } from "@visioni/a3s-alarm-schedules/schema";
import type { AlarmAction, AlarmRoutine, ArmedResponse } from "@src/services/api/types";
import type { RequestErrorType } from "@src/services/api/error-type";
import { cameraScope, type CameraScope } from "@src/features/cameras/analytics";

/** An upsell request was accepted by the backend (`MonitoringUpgradeDialog`, web and native). */
export const MonitoringUpgradeRequested = defineEvent<{ product_interest: ProductInterest }>(
  "Monitoring Upgrade Requested",
);

// Saved and Failed events are tracked in the alarm mutation hooks (`services/api/hooks.ts`,
// and `useManageAlarmResponse` for site responses), so web and native share one call and
// only saves the backend accepted are counted. Their scope is the save's own subject, from
// the mutation's variables, not the route.

/** A save on one camera. */
export type CameraLevel = CameraScope & { level: "camera" };

/**
 * A save across a site's cameras. `camera_count` is how many cameras it applied to;
 * absent when that is unknown (a failed site override).
 */
export type SiteLevel = {
  tenant_id: string | undefined;
  site_id: string | undefined;
  level: "site";
  camera_count: number | undefined;
};

export type AlarmLevel = CameraLevel | SiteLevel;

export function cameraLevel(tenantId: string, siteId: string, cameraKey: string): CameraLevel {
  return { ...cameraScope(tenantId, siteId, cameraKey), level: "camera" };
}

export function siteLevel(tenantId: string, siteId: string, cameraCount: number | undefined): SiteLevel {
  const { tenant_id, site_id } = cameraScope(tenantId, siteId, undefined);
  return { tenant_id, site_id, level: "site", camera_count: cameraCount };
}

/**
 * An override (arm / disarm for a while) was set, extended or removed.
 * `extended` is camera-only (the adjust sheet). `override_action` is absent on `removed`.
 */
export type OverrideChange = {
  action: "created" | "extended" | "removed";
  override_action: AlarmAction | undefined;
};
export const AlarmOverrideSaved = defineEvent<AlarmLevel & OverrideChange>("Alarm Override Saved");
export const AlarmOverrideFailed = defineEvent<AlarmLevel & OverrideChange & { error_type: RequestErrorType }>(
  "Alarm Override Failed",
);

/** A silence was set, extended or removed (camera-only). `minutes` is absent on `removed`. */
export type SilenceChange = {
  action: "created" | "extended" | "removed";
  minutes: number | undefined;
};
export const AlarmSilenceSaved = defineEvent<CameraLevel & SilenceChange>("Alarm Silence Saved");
export const AlarmSilenceFailed = defineEvent<CameraLevel & SilenceChange & { error_type: RequestErrorType }>(
  "Alarm Silence Failed",
);

/**
 * Props of a failed save: what was attempted, plus why it failed. At site level
 * `camera_count` is the cameras attempted and `failed_count` how many of them failed;
 * `failed_count` is absent when the whole request failed before reaching the cameras.
 */
type SaveFailure = { error_type: RequestErrorType; failed_count?: number };

type RoutineChange = { routine_kind: AlarmRoutine["kind"] };
export const AlarmRoutineSaved = defineEvent<AlarmLevel & RoutineChange>("Alarm Routine Saved");
export const AlarmRoutineFailed = defineEvent<AlarmLevel & RoutineChange & SaveFailure>("Alarm Routine Failed");

/**
 * An exception was created, updated or removed (`removed` is camera-only).
 * No `exception_id`: site-made ids embed the exception's free-text reason.
 */
export type ExceptionChange = {
  action: "created" | "updated" | "removed";
  exception_action: AlarmAction | undefined;
};
export const AlarmExceptionSaved = defineEvent<AlarmLevel & ExceptionChange>("Alarm Exception Saved");
export const AlarmExceptionFailed = defineEvent<AlarmLevel & ExceptionChange & SaveFailure>("Alarm Exception Failed");

/** The saved sirens and strobes. Absent at site level, where each camera may differ. */
type ResponseChange = { sirens: ArmedResponse["sirens"] | undefined; strobes: ArmedResponse["strobes"] | undefined };
export const AlarmResponseSaved = defineEvent<AlarmLevel & ResponseChange>("Alarm Response Saved");
export const AlarmResponseFailed = defineEvent<AlarmLevel & ResponseChange & SaveFailure>("Alarm Response Failed");

/** `timezone` is an IANA zone id. */
type TimezoneChange = { timezone: string };
export const AlarmTimezoneSaved = defineEvent<CameraLevel & TimezoneChange>("Alarm Timezone Saved");
export const AlarmTimezoneFailed = defineEvent<CameraLevel & TimezoneChange & { error_type: RequestErrorType }>(
  "Alarm Timezone Failed",
);

/** A settings screen was opened; nothing changed yet. `target` is the routine screen or the customiser. */
export const AlarmRoutineOpened = defineEvent<CameraScope & { target: "routine" | "customise"; source?: "suggestion" }>(
  "Alarm Routine Opened",
);
type ExceptionMode = { mode: "create" | "edit" };
export const AlarmExceptionOpened = defineEvent<CameraScope & ExceptionMode>("Alarm Exception Opened");
export const AlarmResponseOpened = defineEvent<CameraScope>("Alarm Response Opened");
type OverrideMode = { mode: "override" | "adjust" };
export const AlarmOverrideOpened = defineEvent<CameraScope & OverrideMode>("Alarm Override Opened");
/** The silence sheet only confirms a fixed 30 minutes, so it has no Edited event. */
export const AlarmSilenceOpened = defineEvent<CameraScope>("Alarm Silence Opened");

/** The first actual change on a screen, before any save. */
export const AlarmRoutineEdited = defineEvent<CameraScope>("Alarm Routine Edited");
export const AlarmExceptionEdited = defineEvent<CameraScope & ExceptionMode>("Alarm Exception Edited");
export const AlarmOverrideEdited = defineEvent<CameraScope & OverrideMode>("Alarm Override Edited");
export const AlarmResponseEdited = defineEvent<CameraScope & { feature_id: AlarmFeatures }>("Alarm Response Edited");
